import { Button } from "@/components/ui/button"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

interface QuestionMapContentProps {
    total: number;
    currentIndex: number;
    onJump: (index: number) => void;
    // onClose is optional now as Popover usually handles outside click, 
    // but passing it allows explicit close on selection.
    onClose?: () => void;
    getQuestionStatus: (index: number) => { isMastered: boolean; isTried: boolean; isMarked: boolean };
}

export function QuestionMapContent({ total, currentIndex, onJump, onClose, getQuestionStatus }: QuestionMapContentProps) {
    return (
        <div className="flex flex-col max-h-[60vh] w-[300px] sm:w-[400px]">
            <div className="flex items-center justify-between mb-4 shrink-0 px-4 pt-2">
                <h3 className="font-bold text-lg">Question Map</h3>
                {onClose && (
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            <div className="flex items-center gap-x-4 gap-y-3 text-sm mb-4 text-muted-foreground flex-wrap shrink-0 px-4">
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full border-2 border-[#935BE2] bg-[#935BE2]" />
                    Current
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm border border-green-500 bg-green-50" />
                    Mastered
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm border border-yellow-500 bg-yellow-50" />
                    Tried
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm border border-purple-500 bg-purple-50" />
                    Marked
                </div>
                <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm border" />
                    Not Tried
                </div>
            </div>

            <div className="overflow-y-auto min-h-0 p-4">
                <div className="grid grid-cols-5 gap-2">
                    {Array.from({ length: total }).map((_, i) => {
                        const status = getQuestionStatus(i);
                        const isCurrent = i === currentIndex;

                        return (
                            <button
                                key={i}
                                onClick={() => {
                                    onJump(i)
                                    onClose?.()
                                }}
                                className={cn(
                                    "h-12 text-base font-medium rounded-md border flex items-center justify-center transition-colors hover:bg-muted",
                                    isCurrent
                                        ? "border-[#935BE2] bg-[#935BE2] text-white hover:bg-[#935BE2]"
                                        : status.isMastered
                                            ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100"
                                            : status.isMarked
                                                ? "border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100"
                                                : status.isTried
                                                    ? "border-yellow-500 bg-yellow-50 text-yellow-700 hover:bg-yellow-100"
                                                    : "text-foreground bg-card"
                                )}
                            >
                                {i + 1}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
